import { useSettings } from "@/context/SettingsContext";
import { ScrollText } from "lucide-react";

const BRAND_NAME = "Dadu's Achaar";
const BRAND_DOMAIN = "dadusachaar.com";
const BRAND_ADDRESS = "Delhi NCR, India";

function PolicyContainer({ title, subtitle, lastUpdated, testid, children }) {
  const { brand_email, brand_phone } = useSettings();
  return (
    <div className="bg-[#FBF8F3] min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24" data-testid={testid}>
        <div className="mb-10 pb-8 border-b border-[#B8B0A6]/40">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#9E3B21] font-semibold mb-4">
            <ScrollText className="w-4 h-4" /> Legal
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#1E1B18] mb-3">{title}</h1>
          {subtitle && <p className="text-base text-[#5F5A52]">{subtitle}</p>}
          <p className="text-xs text-[#8A8177] mt-4">Last updated: {lastUpdated}</p>
        </div>
        <div className="prose prose-lg max-w-none policy-body">
          {children}
          <div className="mt-16 pt-8 border-t border-[#B8B0A6]/40">
            <h3 className="font-serif text-xl text-[#1E1B18] mb-3">Questions?</h3>
            <p className="text-sm text-[#5F5A52]">
              Email us at <a href={`mailto:${brand_email || "hello@dadusachaar.com"}`} className="text-[#9E3B21] underline">{brand_email || "hello@dadusachaar.com"}</a>
              {brand_phone ? <> or call <a href={`tel:+91${brand_phone}`} className="text-[#9E3B21] underline">+91 {brand_phone}</a></> : null}. We reply within one business day.
            </p>
          </div>
        </div>
      </div>
      <style>{`
        .policy-body p, .policy-body li { color: #3D3A34; line-height: 1.75; font-size: 0.98rem; margin: 0.5rem 0; }
        .policy-body h2 { font-family: 'Fraunces', 'Playfair Display', serif; font-size: 1.6rem; color: #1E1B18; margin-top: 2.5rem; margin-bottom: 0.75rem; }
        .policy-body h3 { font-family: 'Fraunces', serif; font-size: 1.2rem; color: #1E1B18; margin-top: 1.75rem; margin-bottom: 0.5rem; }
        .policy-body ul { list-style-type: disc; padding-left: 1.5rem; margin: 0.5rem 0; }
        .policy-body strong { color: #1E1B18; }
      `}</style>
    </div>
  );
}

export function RefundPolicy() {
  return (
    <PolicyContainer
      title="Refund & Cancellation Policy"
      subtitle={`How we handle refunds, cancellations and returns at ${BRAND_NAME}.`}
      lastUpdated="February 1, 2026"
      testid="refund-policy-page"
    >
      <h2>1. Order Cancellations</h2>
      <p>
        Because every jar of achaar is hand-packed in small batches, we begin preparing your order shortly after it is placed.
      </p>
      <ul>
        <li><strong>Within 2 hours of placing the order</strong> — full cancellation and 100% refund. Email us at hello@dadusachaar.com with your order number.</li>
        <li><strong>After 2 hours but before dispatch</strong> — cancellation is at our discretion. If the batch hasn't been packed we will refund in full.</li>
        <li><strong>After dispatch</strong> — the order cannot be cancelled. You may still initiate a return under our return policy below.</li>
      </ul>

      <h2>2. Returns</h2>
      <p>
        Food products are perishable and, for hygiene reasons, we do <strong>not</strong> accept returns of opened jars. We accept returns only in the following cases:
      </p>
      <ul>
        <li>The jar arrived <strong>broken, leaking, or tampered with</strong>.</li>
        <li>You received the <strong>wrong product</strong> or an <strong>incomplete order</strong>.</li>
        <li>The product is <strong>past its printed best-before date</strong> at the time of delivery.</li>
      </ul>
      <p>
        In any of the above cases, please write to <strong>hello@dadusachaar.com</strong> within <strong>48 hours of delivery</strong> with:
      </p>
      <ul>
        <li>Your order number</li>
        <li>2–3 clear photos of the issue (including the batch label if the jar is intact)</li>
        <li>A short description of what went wrong</li>
      </ul>

      <h2>3. Refunds</h2>
      <p>Once your return is approved, refunds are processed as follows:</p>
      <ul>
        <li><strong>Razorpay (UPI / Card / Netbanking / Wallet)</strong> — refunded to the original payment method within <strong>5–7 business days</strong>. You will receive an email confirmation from Razorpay and from us.</li>
        <li><strong>Cash on Delivery</strong> — refunded via bank transfer or UPI to an account/handle of your choice, within <strong>5–7 business days</strong> of return approval.</li>
      </ul>
      <p>
        For partial issues (e.g. 1 broken jar out of 6 in a Family Pack) we refund only the affected line item plus a fair share of shipping.
      </p>

      <h2>4. Non-Refundable Situations</h2>
      <ul>
        <li>Change of mind after the order has been dispatched.</li>
        <li>Refusal to accept the delivery without a valid reason (address error, unavailability, etc.).</li>
        <li>Complaints raised more than 48 hours after delivery.</li>
        <li>Damage caused by improper storage after delivery (achaar should be kept dry, in a cool place, with a clean spoon).</li>
      </ul>

      <h2>5. Contact for Refunds</h2>
      <p>
        All refund and cancellation requests must be sent to <strong>hello@dadusachaar.com</strong> or via our <a href="/contact" className="text-[#9E3B21] underline">Contact page</a>. We aim to respond within <strong>one business day</strong>.
      </p>
    </PolicyContainer>
  );
}

export function TermsAndConditions() {
  return (
    <PolicyContainer
      title="Terms & Conditions"
      subtitle={`The terms under which ${BRAND_NAME} sells its products through ${BRAND_DOMAIN}.`}
      lastUpdated="February 1, 2026"
      testid="terms-page"
    >
      <p>
        Welcome to {BRAND_NAME}. By browsing this website and placing an order with us, you agree to the following terms. Please read them carefully.
      </p>

      <h2>1. About Us</h2>
      <p>
        {BRAND_NAME} is a home-run kitchen based in {BRAND_ADDRESS}, hand-crafting small-batch achaar using traditional recipes and organic cold-pressed mustard oil. All products are made in a residential kitchen following food-safety best practices.
      </p>

      <h2>2. Products</h2>
      <ul>
        <li>All products are prepared to order or in small daily batches. Slight variations in colour, texture and taste between batches are natural and not a defect.</li>
        <li>Ingredients are listed on every product page. Please read them if you have any known allergies (mustard, sesame, gluten, dairy).</li>
        <li>Prices are listed in Indian Rupees (INR) and include applicable taxes unless stated otherwise.</li>
      </ul>

      <h2>3. Orders & Acceptance</h2>
      <p>
        Placing an order on {BRAND_DOMAIN} is an offer to purchase. Your order is accepted only when you receive a confirmation email from us. We reserve the right to cancel any order at our sole discretion (for example: incomplete address, suspected fraud, service-area restrictions) with a full refund.
      </p>

      <h2>4. Payments</h2>
      <ul>
        <li>Online payments are processed by <strong>Razorpay</strong>. We do not see or store your card / UPI details.</li>
        <li><strong>Cash on Delivery (COD)</strong> is available for eligible orders within our delivery zone.</li>
        <li>You are responsible for providing accurate billing and delivery information.</li>
      </ul>

      <h2>5. Delivery Zone</h2>
      <p>
        We currently deliver only within <strong>Delhi NCR</strong>. Orders outside this zone will be cancelled and refunded. See our <a href="/shipping-policy" className="text-[#9E3B21] underline">Shipping & Delivery Policy</a> for timelines and fees.
      </p>

      <h2>6. Cancellations, Returns & Refunds</h2>
      <p>
        Please refer to our <a href="/refund-policy" className="text-[#9E3B21] underline">Refund & Cancellation Policy</a> for full details.
      </p>

      <h2>7. Intellectual Property</h2>
      <p>
        All content on {BRAND_DOMAIN} — brand name, logo, product photography, recipes, illustrations, and copy — is the property of {BRAND_NAME} and may not be copied, reproduced or used commercially without written permission.
      </p>

      <h2>8. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by law, {BRAND_NAME}'s total liability for any order is limited to the total value of that order. We are not liable for indirect or consequential losses.
      </p>

      <h2>9. Governing Law</h2>
      <p>
        These terms are governed by the laws of India. Any dispute shall be subject to the exclusive jurisdiction of the courts of Delhi, India.
      </p>

      <h2>10. Changes to These Terms</h2>
      <p>
        We may update these terms from time to time. The "Last updated" date at the top of this page will always reflect the latest revision. Continued use of the site after changes constitutes acceptance.
      </p>
    </PolicyContainer>
  );
}

export function ShippingPolicy() {
  return (
    <PolicyContainer
      title="Shipping & Delivery Policy"
      subtitle="Timelines, delivery zones, courier partners, and packaging."
      lastUpdated="February 1, 2026"
      testid="shipping-policy-page"
    >
      <h2>1. Where We Deliver</h2>
      <p>
        {BRAND_NAME} currently ships only within <strong>Delhi NCR</strong> — this includes Delhi, Gurgaon, Noida, Ghaziabad and Faridabad. If your pincode is outside this zone the checkout will not allow the order.
      </p>

      <h2>2. Order Preparation</h2>
      <p>
        Every jar is packed to order. Once you place an order we begin sourcing fresh ingredients and hand-packing the jar. Standard preparation time is:
      </p>
      <ul>
        <li><strong>Individual jars</strong>: 24–48 hours from confirmation.</li>
        <li><strong>Gift Box (3 jars) / Family Pack (6 jars)</strong>: 48–72 hours from confirmation.</li>
        <li><strong>Festive periods</strong>: preparation may extend to 3–4 days — the current lead-time is shown at checkout.</li>
      </ul>

      <h2>3. Delivery Timelines</h2>
      <p>After dispatch, expected delivery times within Delhi NCR are:</p>
      <ul>
        <li><strong>Delhi (all zones)</strong>: 1 business day.</li>
        <li><strong>Gurgaon / Noida / Ghaziabad / Faridabad</strong>: 1–2 business days.</li>
      </ul>
      <p>You will receive a tracking link over SMS/WhatsApp once your order is handed to our courier partner.</p>

      <h2>4. Shipping Fees</h2>
      <ul>
        <li><strong>Free shipping</strong> on orders above the threshold shown in your cart.</li>
        <li>Below the threshold, a flat shipping fee is applied at checkout.</li>
        <li>COD orders may attract a small COD-handling fee, shown at checkout.</li>
      </ul>

      <h2>5. Packaging</h2>
      <p>
        Every jar is sealed with a food-grade cap and secondary tamper-evident tape, wrapped in bubble padding, and shipped in a rigid outer carton. If the outer box arrives visibly damaged, please refuse the delivery or open it in front of the courier and photograph any damage — this speeds up refunds.
      </p>

      <h2>6. Missed Deliveries</h2>
      <p>
        Our courier will attempt delivery up to 2 times. After 2 failed attempts, the parcel is returned to us and we will contact you to reschedule (small re-delivery fee may apply) or refund the product amount excluding shipping.
      </p>

      <h2>7. Wrong Address / Address Change</h2>
      <p>
        Please double-check your address at checkout. Changes are possible only before dispatch — write to <strong>hello@dadusachaar.com</strong> with your order number as soon as possible.
      </p>
    </PolicyContainer>
  );
}

export function PrivacyPolicy() {
  return (
    <PolicyContainer
      title="Privacy Policy"
      subtitle={`How ${BRAND_NAME} collects, uses and protects your personal information.`}
      lastUpdated="February 1, 2026"
      testid="privacy-policy-page"
    >
      <h2>1. What We Collect</h2>
      <p>When you place an order or contact us, we collect:</p>
      <ul>
        <li><strong>Contact details</strong>: name, phone number, email address.</li>
        <li><strong>Delivery address</strong>: house/flat, area, city, pincode, landmark.</li>
        <li><strong>Order details</strong>: products purchased, quantities, price, coupon usage.</li>
        <li><strong>Payment metadata</strong>: transaction ID and status. <strong>We never see or store your card, UPI PIN, or netbanking credentials</strong> — those are handled directly by Razorpay.</li>
        <li><strong>Optional</strong>: any message you leave in the "Order Notes" or Contact form.</li>
      </ul>

      <h2>2. How We Use It</h2>
      <ul>
        <li>To prepare, ship and confirm your order.</li>
        <li>To send order confirmations and delivery updates over email, SMS or WhatsApp.</li>
        <li>To respond to your questions and support requests.</li>
        <li>To improve our products (aggregate, anonymised trends only).</li>
        <li>To meet our legal and tax obligations.</li>
      </ul>

      <h2>3. What We Do NOT Do</h2>
      <ul>
        <li>We do <strong>not</strong> sell your data to third parties.</li>
        <li>We do <strong>not</strong> run ad-tracking or share your info with advertising networks.</li>
        <li>We do <strong>not</strong> send marketing emails unless you have opted in.</li>
      </ul>

      <h2>4. Third-Party Services We Rely On</h2>
      <ul>
        <li><strong>Razorpay</strong> — payment processing. Governed by their own privacy policy.</li>
        <li><strong>Resend</strong> — transactional order emails.</li>
        <li><strong>Courier partners</strong> — receive only the name, phone and delivery address needed to deliver your order.</li>
      </ul>

      <h2>5. Data Retention</h2>
      <p>
        Order records are retained for up to <strong>7 years</strong> to meet Indian tax and accounting requirements. You may request deletion of your account and personal contact details at any time by writing to <strong>hello@dadusachaar.com</strong>; order records will be anonymised where legally possible.
      </p>

      <h2>6. Your Rights</h2>
      <ul>
        <li>Request a copy of the data we hold about you.</li>
        <li>Correct any inaccurate information.</li>
        <li>Ask us to delete or anonymise your personal data (subject to legal retention requirements).</li>
        <li>Withdraw consent for marketing communications at any time.</li>
      </ul>

      <h2>7. Cookies</h2>
      <p>
        {BRAND_DOMAIN} uses only essential cookies required to keep your cart working during checkout. We do not use analytics or advertising cookies.
      </p>

      <h2>8. Security</h2>
      <p>
        All traffic to {BRAND_DOMAIN} is served over HTTPS. Payments are processed on Razorpay's PCI-DSS compliant infrastructure. Our admin dashboard is password-protected and used only by the founding team.
      </p>

      <h2>9. Children</h2>
      <p>
        Our services are intended for users aged 18 and above. We do not knowingly collect data from minors.
      </p>

      <h2>10. Changes to This Policy</h2>
      <p>
        We may update this policy from time to time. The "Last updated" date at the top of this page will always reflect the latest revision.
      </p>
    </PolicyContainer>
  );
}
